import { FC, useCallback, useMemo, useState } from 'react';

import { Alert, Button, Form, Modal } from '@payaca/ambrosia';
import ValuedPaymentScheduleSummary from '@payaca/components/paymentScheduleSummary/ValuedPaymentScheduleSummary';
import {
  type EditableStoredPaymentSchedule,
  fromEditableStoredPaymentSchedule,
} from '@payaca/helpers/editableStoredPaymentSchedule';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';

import { toStoredPaymentScheduleStage } from '@/api/fragments/paymentScheduleStageFields';
import useUpdateChangeProposalPaymentSchedule from '@/api/mutations/proposals/useUpdateChangeProposalPaymentSchedule';
import proposalKeys from '@/api/queries/proposal/keyFactor';
import useGetChangeProposal from '@/api/queries/proposal/useGetChangeProposal';
import { useRegion } from '@/api/rest/users/useRegion';
import GrantScheduleNote from '@/ui/components/grantScheduleNote/GrantScheduleNote';
import {
  PAYMENT_SCHEDULE_FORM_SCHEMA,
  PaymentScheduleFormValues,
} from '@/ui/components/paymentScheduleStageCards/types';
import { toPaymentScheduleInput } from '@/utils/paymentScheduleInput';
import EditJobSection from '../editJob/EditJobSection';
import ChangeProposalPaymentScheduleEditor from './ChangeProposalPaymentScheduleEditor';
import {
  type ChangeProposalPaymentScheduleRow,
  getInvoicedStages,
  type InvoicedStage,
  toEditableChangeProposalPaymentSchedule,
} from './editableChangeProposalPaymentSchedule';

type Props = {
  changeProposalId: string;
  futureProjectValue: number;
  /** The send preview renders the REST copy, which this section's save does not refresh. */
  onPaymentScheduleUpdated: () => void;
};

const ChangeProposalPaymentScheduleSection: FC<Props> = (props) => {
  const { changeProposalId, futureProjectValue, onPaymentScheduleUpdated } =
    props;
  const [showEditModal, setShowEditModal] = useState(false);
  const queryClient = useQueryClient();
  const region = useRegion();

  const { data } = useGetChangeProposal(changeProposalId);
  const paymentSchedule = data?.changeProposal?.paymentSchedule;
  const effectivePaymentSchedule =
    data?.changeProposal?.effectivePaymentSchedule;

  const effectiveStages = useMemo(
    () =>
      effectivePaymentSchedule?.stages.map((stage) => ({
        description: stage.description,
        percentageDue:
          stage.__typename === 'DerivedGrantStage'
            ? null
            : (stage.shareOfTotal ?? null),
        isGrant: stage.__typename === 'DerivedGrantStage',
        value: stage.value.value,
      })) ?? [],
    [effectivePaymentSchedule]
  );

  const grantNames = useMemo(
    () =>
      effectiveStages.flatMap((stage) =>
        stage.isGrant ? [stage.description] : []
      ),
    [effectiveStages]
  );

  const { editablePaymentSchedule, invoicedStages } = useMemo(() => {
    if (!paymentSchedule) {
      return { editablePaymentSchedule: null, invoicedStages: [] };
    }
    const { isInSync, explainer } = paymentSchedule;
    const rows = paymentSchedule.stages.map(
      (s): ChangeProposalPaymentScheduleRow => ({
        stage: s.stage ? toStoredPaymentScheduleStage(s.stage) : null,
        invoice: s.invoice,
        invoiceValue: s.invoiceValue?.value,
        invoiceProjectPercentage: s.invoiceProjectPercentage,
      })
    );
    return {
      editablePaymentSchedule: toEditableChangeProposalPaymentSchedule({
        isInSync,
        rows,
        explainer,
      }),
      invoicedStages: getInvoicedStages({ isInSync, rows }),
    };
  }, [paymentSchedule]);

  if (!paymentSchedule) return null;

  return (
    <>
      <EditJobSection
        title={
          <div className="flex flex-row items-baseline justify-between">
            <h2>Payment schedule</h2>
            <Button variant="link" onClick={() => setShowEditModal(true)}>
              {paymentSchedule.isInSync
                ? 'Edit payment schedule'
                : 'Update payment schedule'}
            </Button>
          </div>
        }
      >
        {!paymentSchedule.isInSync ? (
          <Alert colour="yellow">
            The payment schedule is out of sync with the change proposal value.
            Please update or discard it before sending.
          </Alert>
        ) : (
          <ValuedPaymentScheduleSummary
            stages={effectiveStages}
            explainer={effectivePaymentSchedule?.explainer}
            region={region}
          />
        )}
      </EditJobSection>
      <Modal
        size="lg"
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Edit payment schedule"
      >
        {editablePaymentSchedule && (
          <EditChangeProposalPaymentScheduleModalBody
            changeProposalId={changeProposalId}
            futureProjectValue={futureProjectValue}
            grantNames={grantNames}
            invoicedStages={invoicedStages}
            paymentSchedule={editablePaymentSchedule}
            onSuccess={() => {
              setShowEditModal(false);
              onPaymentScheduleUpdated();
              void queryClient.invalidateQueries({
                queryKey: proposalKeys.changeProposal(changeProposalId),
              });
            }}
          />
        )}
      </Modal>
    </>
  );
};

const EditChangeProposalPaymentScheduleModalBody: FC<{
  changeProposalId: string;
  futureProjectValue: number;
  grantNames: string[];
  invoicedStages: InvoicedStage[];
  paymentSchedule: EditableStoredPaymentSchedule;
  onSuccess?: () => void;
}> = (props) => {
  const {
    changeProposalId,
    futureProjectValue,
    grantNames,
    invoicedStages,
    paymentSchedule,
    onSuccess,
  } = props;

  const { mutate: updatePaymentSchedule, isPending: isUpdating } =
    useUpdateChangeProposalPaymentSchedule({ onSuccess });

  const methods = useForm<PaymentScheduleFormValues>({
    defaultValues: { paymentSchedule },
    resolver: zodResolver(PAYMENT_SCHEDULE_FORM_SCHEMA),
    mode: 'onChange',
  });

  const onDelete = useCallback(() => {
    updatePaymentSchedule({ changeProposalId, paymentSchedule: null });
  }, [changeProposalId, updatePaymentSchedule]);

  const onSubmit = (values: PaymentScheduleFormValues) => {
    const stored = fromEditableStoredPaymentSchedule(values.paymentSchedule);
    updatePaymentSchedule({
      changeProposalId,
      paymentSchedule: stored?.stages.length
        ? toPaymentScheduleInput(stored)
        : null,
    });
  };

  return (
    <Form methods={methods} onSubmit={onSubmit}>
      <Modal.Body className="flex flex-col gap-4">
        {grantNames.length > 0 && <GrantScheduleNote grantNames={grantNames} />}
        <ChangeProposalPaymentScheduleEditor
          futureProjectValue={futureProjectValue}
          invoicedStages={invoicedStages}
        />
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Actions>
          <Button
            isProcessing={isUpdating}
            onClick={onDelete}
            variant="secondary"
            colour="negative"
          >
            Delete
          </Button>
          <Button type="submit" isProcessing={isUpdating}>
            Save
          </Button>
        </Modal.Footer.Actions>
      </Modal.Footer>
    </Form>
  );
};

export default ChangeProposalPaymentScheduleSection;
